import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RefreshIcon from '@mui/icons-material/Refresh';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';
import useIsMobile from '@/lib/useIsMobile';
import ChipInputField from '@/components/ChipInputField';
import LeadFormRenderer from '@/components/LeadFormRenderer';

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Paragraph' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'heading', label: 'Section heading' },
];

// Types whose input can usefully show placeholder text.
const PLACEHOLDER_TYPES = new Set(['text', 'textarea', 'number', 'email', 'phone']);
const OPTION_TYPES = new Set(['select', 'multiselect']);

const slugify = (label) =>
  (label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// FastAPI 422s arrive as an array of {msg}; plain errors as a string detail.
const errText = (err, fallback) => {
  const detail = err.response?.data?.detail;
  return (Array.isArray(detail) ? detail[0]?.msg : detail) || fallback;
};

// Seeded / never-saved forms can have sparse settings dicts — fill every key.
const normalizeSettings = (s) => ({
  title: s?.title || '',
  intro: s?.intro || '',
  thank_you_message: s?.thank_you_message || '',
  redirect_url: s?.redirect_url || '',
  show_notes: Boolean(s?.show_notes),
  tags: Array.isArray(s?.tags) ? s.tags : [],
  assign_site_id: s?.assign_site_id || '',
  assign_trainer_ids: Array.isArray(s?.assign_trainer_ids) ? s.assign_trainer_ids : [],
  notify: s?.notify !== false,
});

const formatLastSubmission = (iso) => {
  if (!iso) return 'No submissions yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No submissions yet';
  return `Last: ${d.toLocaleDateString()}`;
};

// Manages the tenant's public lead-capture forms: list + stats, per-form field
// builder with drag-to-reorder, submission settings, live preview and sharing
// (link / QR / iframe embed). Replaces the old single-form field manager +
// link card.
const LeadFormsManager = () => {
  const { isOwner } = useAuth();
  const isMobile = useIsMobile();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [fieldDeleteIdx, setFieldDeleteIdx] = useState(null);
  // Client-side row ids so reorder/delete don't recycle React keys.
  const nextRowId = useRef(1);
  const withRowId = (f) => ({
    ...f,
    options: Array.isArray(f.options) ? f.options : [],
    _rowId: nextRowId.current++,
  });

  const load = async () => {
    const { data } = await api.get('/lead-forms');
    setForms(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/lead-forms');
        if (!cancelled) setForms(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) toast.error('Failed to load lead forms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    api
      .get('/sites')
      .then((res) => !cancelled && setSites(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
    api
      .get('/users')
      .then(
        (res) =>
          !cancelled &&
          setUsers((Array.isArray(res.data) ? res.data : []).filter((u) => u.role !== 'member')),
      )
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = forms.find((f) => f.form_id === selectedId) || null;

  const initDraft = (form) => {
    setDraft({
      name: form.name || '',
      fields: (Array.isArray(form.fields) ? form.fields : []).map(withRowId),
      settings: normalizeSettings(form.settings),
    });
  };

  const selectForm = (form) => {
    if (selectedId === form.form_id) {
      setSelectedId(null);
      setDraft(null);
      return;
    }
    setSelectedId(form.form_id);
    initDraft(form);
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error('Give the form a name first');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/lead-forms', { name });
      setForms((prev) => [...prev, data]);
      setCreateName('');
      setSelectedId(data.form_id);
      initDraft(data);
      toast.success('Form created');
    } catch (err) {
      toast.error(errText(err, 'Failed to create form'));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (form, checked) => {
    try {
      const { data } = await api.put(`/lead-forms/${form.form_id}`, { is_active: checked });
      setForms((prev) => prev.map((f) => (f.form_id === data.form_id ? data : f)));
      toast.success(checked ? 'Form activated' : 'Form deactivated — its link stops working');
    } catch (err) {
      toast.error(errText(err, 'Failed to update form'));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/lead-forms/${deleteTarget.form_id}`);
      toast.success(data?.message || (data?.deleted ? 'Form deleted' : 'Form deactivated'));
      if (selectedId === deleteTarget.form_id) {
        setSelectedId(null);
        setDraft(null);
      }
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(errText(err, 'Failed to delete form'));
    } finally {
      setDeleting(false);
    }
  };

  const regenerate = async () => {
    if (!selected) return;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        'Generate a new link? The current URL (and any printed QR codes or embeds) will stop working.',
      )
    ) {
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await api.post(`/lead-forms/${selected.form_id}/regenerate`);
      setForms((prev) =>
        prev.map((f) =>
          f.form_id === selected.form_id ? { ...f, token: data.token, url: data.url } : f,
        ),
      );
      toast.success('New link generated');
    } catch (err) {
      toast.error(errText(err, 'Failed to regenerate link'));
    } finally {
      setRegenerating(false);
    }
  };

  // ----- draft field editing -----

  const updateField = (idx, patch) =>
    setDraft((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    }));

  const addField = () =>
    setDraft((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        withRowId({ key: '', label: '', type: 'text', required: false, options: [] }),
      ],
    }));

  const removeField = (idx) =>
    setDraft((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }));

  const moveField = (idx, dir) =>
    setDraft((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.fields.length) return prev;
      const next = [...prev.fields];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...prev, fields: next };
    });

  const requestFieldDelete = (idx) => {
    const f = draft.fields[idx];
    // Headings and never-saved fields carry no stored lead data — skip the warning.
    if (f.type === 'heading' || !(f.key || '').trim()) {
      removeField(idx);
      return;
    }
    setFieldDeleteIdx(idx);
  };

  // HTML5 drag-and-drop reorder (same pattern as the Leads kanban). The drag
  // handle alone is draggable so text inputs inside the row keep selection;
  // the up/down arrows stay as the touch/mobile fallback.
  const handleFieldDragStart = (e, idx) => {
    e.dataTransfer.setData('fieldIdx', String(idx));
  };
  const handleFieldDragOver = (e) => {
    e.preventDefault();
  };
  const handleFieldDrop = (e, targetIdx) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('fieldIdx');
    if (raw === '') return;
    const from = Number(raw);
    if (Number.isNaN(from) || from === targetIdx) return;
    setDraft((prev) => {
      if (from >= prev.fields.length) return prev;
      const next = [...prev.fields];
      const [moved] = next.splice(from, 1);
      next.splice(targetIdx, 0, moved);
      return { ...prev, fields: next };
    });
  };

  const setSetting = (patch) =>
    setDraft((prev) => ({ ...prev, settings: { ...prev.settings, ...patch } }));

  // ----- save -----

  const handleSave = async () => {
    if (!selected || !draft) return;
    const name = (draft.name || '').trim();
    if (!name) {
      toast.error('The form needs a name');
      return;
    }
    // Register every pre-existing key up front so a newly slugged key can
    // never steal a saved key from another row (which would silently remap
    // stored member answers onto the wrong field).
    const usedKeys = new Set(
      draft.fields.map((f) => (f.key || '').trim()).filter(Boolean)
    );
    const fieldsPayload = [];
    for (const f of draft.fields) {
      const label = (f.label || '').trim();
      if (!label) {
        toast.error('Every field needs a label');
        return;
      }
      const type = f.type || 'text';
      if (OPTION_TYPES.has(type) && !(f.options || []).some((o) => o.trim())) {
        toast.error(`"${label}" needs at least one option`);
        return;
      }
      // Keep an existing key (renames preserve stored values); otherwise slug
      // the label, suffixing to stay unique so same-named fields can't collide
      // with each other or with any saved key.
      let key = (f.key || '').trim();
      if (!key) {
        const base = slugify(label) || 'field';
        key = base;
        let n = 2;
        while (usedKeys.has(key)) {
          key = `${base}_${n++}`;
        }
        usedKeys.add(key);
      }
      fieldsPayload.push({
        key,
        label,
        type,
        required: type === 'heading' ? false : !!f.required,
        options: OPTION_TYPES.has(type)
          ? (f.options || []).map((o) => o.trim()).filter(Boolean)
          : null,
        placeholder: (f.placeholder || '').trim() || null,
        help_text: (f.help_text || '').trim() || null,
      });
    }
    const s = draft.settings;
    const redirect = (s.redirect_url || '').trim();
    if (redirect && !/^https?:\/\//i.test(redirect)) {
      toast.error('Redirect URL must start with http:// or https://');
      return;
    }
    const settingsPayload = {
      title: (s.title || '').trim() || null,
      intro: (s.intro || '').trim() || null,
      thank_you_message: (s.thank_you_message || '').trim() || null,
      redirect_url: redirect || null,
      show_notes: !!s.show_notes,
      tags: s.tags,
      assign_site_id: s.assign_site_id || null,
      assign_trainer_ids: s.assign_trainer_ids,
      notify: !!s.notify,
    };
    setSaving(true);
    try {
      const { data } = await api.put(`/lead-forms/${selected.form_id}`, {
        name,
        fields: fieldsPayload,
        settings: settingsPayload,
      });
      setForms((prev) => prev.map((f) => (f.form_id === data.form_id ? data : f)));
      initDraft(data);
      toast.success('Form saved');
    } catch (err) {
      toast.error(errText(err, 'Failed to save form'));
    } finally {
      setSaving(false);
    }
  };

  // ----- render -----

  const userName = (id) => users.find((u) => u.user_id === id)?.name || 'Unknown user';
  const embedSnippet = selected
    ? `<iframe src="${selected.url}" style="border:0;width:100%;min-height:640px"></iframe>`
    : '';

  const copyText = async (text, okMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMsg);
    } catch {
      toast.error('Could not copy — select the text and copy manually');
    }
  };

  const renderFieldRow = (f, idx) => (
    <Paper
      key={f._rowId}
      variant="outlined"
      onDrop={(e) => handleFieldDrop(e, idx)}
      onDragOver={handleFieldDragOver}
      sx={{ p: 1.5, borderRadius: 2 }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Box
          draggable
          onDragStart={(e) => handleFieldDragStart(e, idx)}
          aria-label="Drag to reorder"
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            alignSelf: 'center',
            cursor: 'grab',
            color: 'text.disabled',
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Stack direction="column" sx={{ alignSelf: 'center' }}>
          <IconButton
            size="small"
            aria-label="Move field up"
            disabled={idx === 0}
            onClick={() => moveField(idx, -1)}
            sx={{ p: 0.25 }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Move field down"
            disabled={idx === draft.fields.length - 1}
            onClick={() => moveField(idx, 1)}
            sx={{ p: 0.25 }}
          >
            <ArrowDownwardIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>
        <TextField
          label="Label"
          size="small"
          value={f.label}
          onChange={(e) => updateField(idx, { label: e.target.value })}
          sx={{ flex: 1, minWidth: 150, width: { xs: '100%', sm: 'auto' } }}
          data-testid={`field-label-input-${idx}`}
        />
        <TextField
          select
          label="Type"
          size="small"
          value={f.type || 'text'}
          onChange={(e) => updateField(idx, { type: e.target.value })}
          sx={{ width: { xs: '100%', sm: 160 } }}
        >
          {TYPE_OPTIONS.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        {f.type !== 'heading' && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!f.required}
                onChange={(e) => updateField(idx, { required: e.target.checked })}
              />
            }
            label={<Typography variant="body2">Required</Typography>}
            sx={{ mt: 0.25, mr: 0 }}
          />
        )}
        <IconButton
          aria-label="Remove field"
          onClick={() => requestFieldDelete(idx)}
          sx={{ ml: 'auto' }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      {OPTION_TYPES.has(f.type) && (
        <ChipInputField
          label="Options"
          size="small"
          value={f.options || []}
          onChange={(options) => updateField(idx, { options })}
          placeholder="Type an option, press Enter"
          sx={{ mt: 1.5 }}
        />
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
        {PLACEHOLDER_TYPES.has(f.type) && (
          <TextField
            label="Placeholder"
            size="small"
            fullWidth
            value={f.placeholder || ''}
            onChange={(e) => updateField(idx, { placeholder: e.target.value })}
            inputProps={{ maxLength: 200 }}
          />
        )}
        <TextField
          label="Help text"
          size="small"
          fullWidth
          value={f.help_text || ''}
          onChange={(e) => updateField(idx, { help_text: e.target.value })}
          inputProps={{ maxLength: 200 }}
        />
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        Each form has its own share link, fields and settings, so you can track where leads come
        from (walk-ins, Instagram, your website…). Submissions land in the Leads pipeline tagged{' '}
        <code>web-form</code> plus any tags you set per form.
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={96} />
      ) : (
        <Stack spacing={1.25}>
          {forms.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No forms yet — create your first one below.
            </Typography>
          )}
          {forms.map((form) => (
            <Paper
              key={form.form_id}
              variant="outlined"
              sx={{
                p: 1.75,
                borderRadius: 2,
                borderColor: selectedId === form.form_id ? 'primary.main' : 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {form.name}
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 0.75 }}>
                    <Chip size="small" label={`${form.stats?.submissions ?? 0} submissions`} />
                    <Chip size="small" label={`${form.stats?.converted ?? 0} converted`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatLastSubmission(form.stats?.last_submission)}
                    />
                    {!form.is_active && <Chip size="small" color="warning" label="Inactive" />}
                  </Stack>
                </Box>
                <Tooltip title={form.is_active ? 'Active — accepting submissions' : 'Inactive — link disabled'}>
                  <Switch
                    size="small"
                    checked={!!form.is_active}
                    onChange={(e) => toggleActive(form, e.target.checked)}
                    inputProps={{ 'aria-label': `${form.name} active`, 'data-testid': `form-active-toggle-${form.form_id}` }}
                  />
                </Tooltip>
                <Button
                  size="small"
                  variant={selectedId === form.form_id ? 'contained' : 'outlined'}
                  onClick={() => selectForm(form)}
                  data-testid={`edit-form-button-${form.form_id}`}
                >
                  {selectedId === form.form_id ? 'Close' : 'Edit'}
                </Button>
                {isOwner && (
                  <IconButton
                    size="small"
                    aria-label={`Delete ${form.name}`}
                    onClick={() => setDeleteTarget(form)}
                    data-testid={`delete-form-button-${form.form_id}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ maxWidth: 440 }}>
        <TextField
          label="New form name"
          size="small"
          fullWidth
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleCreate();
            }
          }}
          inputProps={{ maxLength: 120 }}
          data-testid="create-form-name-input"
        />
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={handleCreate}
          disabled={creating}
          sx={{ flexShrink: 0 }}
          data-testid="create-form-button"
        >
          {creating ? 'Creating…' : 'Create form'}
        </Button>
      </Stack>

      {selected && draft && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2.5,
            alignItems: 'flex-start',
          }}
        >
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: 'auto' } }}>
            <TextField
              label="Form name"
              size="small"
              value={draft.name}
              onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              inputProps={{ maxLength: 120 }}
              sx={{ maxWidth: 360 }}
              data-testid="form-name-input"
            />

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Fields
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
              Name, email and phone are always asked. Drag the handle (or use the arrows) to
              reorder custom fields.
            </Typography>
            {draft.fields.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No custom fields yet.
              </Typography>
            )}
            {draft.fields.map(renderFieldRow)}
            <Box>
              <Button startIcon={<AddIcon />} onClick={addField} variant="outlined" size="small" data-testid="add-field-button">
                Add field
              </Button>
            </Box>

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Form settings
            </Typography>
            <TextField
              label="Form title"
              size="small"
              fullWidth
              value={draft.settings.title}
              onChange={(e) => setSetting({ title: e.target.value })}
              placeholder="Defaults to your business name"
              inputProps={{ maxLength: 200 }}
              data-testid="form-title-input"
            />
            <TextField
              label="Intro text"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={draft.settings.intro}
              onChange={(e) => setSetting({ intro: e.target.value })}
              placeholder="Leave your details and we'll get back to you."
            />
            <TextField
              label="Thank-you message"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={draft.settings.thank_you_message}
              onChange={(e) => setSetting({ thank_you_message: e.target.value })}
              placeholder="Thanks! We received your info and will be in touch shortly."
            />
            <TextField
              label="Redirect URL"
              size="small"
              fullWidth
              value={draft.settings.redirect_url}
              onChange={(e) => setSetting({ redirect_url: e.target.value })}
              placeholder="https://…"
              helperText="Optional — after submitting, visitors are sent here (must start with http:// or https://)"
            />
            <ChipInputField
              label="Tags for new leads"
              size="small"
              value={draft.settings.tags}
              onChange={(tags) => setSetting({ tags })}
              placeholder="Type a tag, press Enter"
              helperText="Added to every lead from this form, on top of web-form"
            />
            <TextField
              select
              label="Auto-assign location"
              size="small"
              fullWidth
              value={draft.settings.assign_site_id}
              onChange={(e) => setSetting({ assign_site_id: e.target.value })}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.site_id} value={site.site_id}>
                  {site.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControl fullWidth size="small">
              <InputLabel id="assign-trainers-label">Auto-assign staff</InputLabel>
              <Select
                labelId="assign-trainers-label"
                label="Auto-assign staff"
                multiple
                value={draft.settings.assign_trainer_ids}
                onChange={(e) => {
                  const v = e.target.value;
                  setSetting({ assign_trainer_ids: typeof v === 'string' ? v.split(',') : v });
                }}
                renderValue={(vals) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {vals.map((id) => (
                      <Chip key={id} size="small" label={userName(id)} />
                    ))}
                  </Box>
                )}
              >
                {users.map((u) => (
                  <MenuItem key={u.user_id} value={u.user_id}>
                    <Checkbox
                      size="small"
                      checked={draft.settings.assign_trainer_ids.includes(u.user_id)}
                    />
                    <ListItemText primary={u.name} secondary={u.email} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                New leads go to the listed teammate with the fewest open leads
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={draft.settings.show_notes}
                  onChange={(e) => setSetting({ show_notes: e.target.checked })}
                />
              }
              label={<Typography variant="body2">Show an optional “anything else?” notes box</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={draft.settings.notify}
                  onChange={(e) => setSetting({ notify: e.target.checked })}
                />
              }
              label={<Typography variant="body2">Notify staff about new submissions</Typography>}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-lead-form-button"
              >
                {saving ? 'Saving…' : 'Save form'}
              </Button>
            </Box>

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Share
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={selected.url}
                    InputProps={{ readOnly: true }}
                    data-testid="public-form-url"
                  />
                  <Tooltip title="Copy link">
                    <IconButton
                      size="small"
                      onClick={() => copyText(selected.url, 'Link copied')}
                      data-testid="copy-form-url-button"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  value={embedSnippet}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  helperText="Paste this on your website to embed the form"
                  data-testid="embed-snippet"
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyText(embedSnippet, 'Embed code copied')}
                    data-testid="copy-embed-button"
                  >
                    Copy embed code
                  </Button>
                  {isOwner && (
                    <Button
                      startIcon={<RefreshIcon />}
                      onClick={regenerate}
                      disabled={regenerating}
                      size="small"
                      color="inherit"
                      sx={{ color: 'text.secondary' }}
                      data-testid="regenerate-form-url-button"
                    >
                      {regenerating ? 'Generating…' : 'Generate new link'}
                    </Button>
                  )}
                </Box>
              </Stack>
              <Box
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: 'center', sm: 'flex-start' },
                  p: 1.5,
                  bgcolor: '#fff',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <QRCodeSVG value={selected.url} size={128} />
              </Box>
            </Stack>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              width: { xs: '100%', lg: 340 },
              flexShrink: 0,
              position: { xs: 'static', lg: 'sticky' },
              top: { lg: 8 },
            }}
            data-testid="form-preview"
          >
            <Typography variant="overline" color="text.secondary">
              Live preview
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, overflowWrap: 'break-word' }}>
              {draft.settings.title || 'Your business name'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {draft.settings.intro || "Leave your details and we'll get back to you."}
            </Typography>
            <Stack spacing={2}>
              <LeadFormRenderer
                fields={draft.fields}
                form={draft.settings}
                showNotes={draft.settings.show_notes}
                disabled
              />
              <Button variant="contained" size="large" disabled>
                Send
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete “{deleteTarget?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A form that has never received a submission is deleted permanently. A form with
            submissions is deactivated instead — its link stops working, but its leads and stats
            are kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
            data-testid="confirm-delete-form-button"
          >
            {deleting ? 'Deleting…' : 'Delete form'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fieldDeleteIdx !== null}
        onClose={() => setFieldDeleteIdx(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Remove “{fieldDeleteIdx !== null ? draft?.fields?.[fieldDeleteIdx]?.label || 'field' : ''}”?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Values already stored on existing leads aren&apos;t deleted, but they will no longer be
            shown anywhere. The change applies when you save the form.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDeleteIdx(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              removeField(fieldDeleteIdx);
              setFieldDeleteIdx(null);
            }}
            data-testid="confirm-delete-field-button"
          >
            Remove field
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default LeadFormsManager;
